/**
 * Самописный error reporter для admin-web.
 * Шлёт ошибки в `${apiUrl}/api/v1/errors/report`, где apiUrl зависит от текущего env (prod/dev).
 */

import { getApiUrl } from '@/shared/runtimeConfig';
import { store } from '@/app/store';

const DEDUP_LIMIT = 50;
const sessionFingerprints = new Set<string>();

interface ReportPayload {
  source: 'admin-web';
  level?: 'error' | 'warning' | 'info';
  type?: string | null;
  message: string;
  stack?: string | null;
  url?: string | null;
  userAgent?: string | null;
  release?: string | null;
  context?: Record<string, unknown> | null;
}

function fingerprint(message: string, stack: string | null | undefined): string {
  const stackLine = (stack ?? '')
    .split('\n')
    .map((l) => l.trim())
    .find((l) => /\.[tj]sx?:|^at /i.test(l));
  return `${message.slice(0, 100)}|${stackLine ?? ''}`;
}

function getReportUrl(): string {
  const env = store.getState().auth.currentEnv;
  return `${getApiUrl(env)}/api/v1/errors/report`;
}

function send(payload: ReportPayload): void {
  fetch(getReportUrl(), {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
    keepalive: true,
    credentials: 'omit',
  }).catch(() => {
    /* swallow */
  });
}

export function report(error: unknown, context?: Record<string, unknown>): void {
  const message = error instanceof Error ? error.message : String(error);
  const stack = error instanceof Error ? (error.stack ?? null) : null;
  const type = error instanceof Error ? error.constructor.name : 'Unknown';

  const fp = fingerprint(message, stack);
  if (sessionFingerprints.has(fp)) return;
  if (sessionFingerprints.size < DEDUP_LIMIT) sessionFingerprints.add(fp);

  send({
    source: 'admin-web',
    level: 'error',
    type,
    message,
    stack,
    url: typeof window !== 'undefined' ? window.location.href : null,
    userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
    release: (import.meta.env.VITE_RELEASE as string | undefined) ?? null,
    context: context ?? null,
  });
}

export function initErrorReporter(): void {
  if (typeof window === 'undefined') return;

  window.addEventListener('error', (event) => {
    report(event.error ?? new Error(event.message ?? 'Unknown error'), {
      filename: event.filename,
      lineno: event.lineno,
      colno: event.colno,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = event.reason;
    const error = reason instanceof Error ? reason : new Error(String(reason));
    report(error, { reason: 'unhandledrejection' });
  });
}
